// Admin table for the driver onboarding-wizard step groups: a MODE per group
// (Default / Required / Optional / Hidden) and their ORDER, edited from
// /settings in the portal. Pure component — everything comes in through
// props (data = the /settings draft the Onboarding wizard tab is editing,
// setData, disabled, t, isRTL) and the resolver in common/src/other/
// onboarding.js (api.resolveStepModes / api.resolveStepOrder /
// api.legacyStepMode) is the single source of truth for what a choice means,
// so the "Effective" column shows exactly what the mobile wizard and the
// complete_onboarding Cloud Function will enforce.
//
// What each choice writes:
//   Required / Optional / Hidden -> data[onboardingStep<Id>] = 'required' |
//     'optional' | 'off' (api.onboardingStepSettingKey).
//   Default -> the key is REMOVED from data. editSettings (common/src/
//     actions/settingsactions.js) sends every key that was loaded but is
//     absent from the saved object as null, which deletes it from /settings;
//     the resolver then falls back to the legacy switch the step follows.
//   Order arrows -> data.onboardingStepOrder = full sanitized CSV of group
//     ids from api.resolveStepOrder (never a partial list).
//   Reset order -> data.onboardingStepOrder removed (deleted on save).
//
// Look (wave 3): theme tokens, status chips with an icon and a word, a
// select with one accessible name per row, arrow buttons for the order.
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { api } from 'common';
import { tokens as c } from '../theme';
import StatusChip from './modern/StatusChip';

const DEFAULT = 'default';
// Groups without order arrows: name is pinned first, extras pinned last,
// carPhoto always follows vehicle (the resolver re-inserts it).
const PINNED = { name: true, extras: true, carPhoto: true };
// This table is the single rule for drivers: the wizard, Finish
// (complete_onboarding), the driver-home go-online switch and its red rows,
// the Done-step "Start driving", the accept-trip ID check and the server's
// dispatch eligibility all read the same effective modes
// (api.driverOnlineBlockers / eligibilitySignature). The classic switches on
// this page only supply each row's Default. Two things the table does NOT
// decide, shown as row notes: the TIN blocks going online only through
// "Require TIN before a driver can go online", and regular bookings are
// matched by car type — a driver with no car type on file is offered every
// car type (Donkey Meter / offline meter are unaffected).

const mutedSx = { fontSize: 13, color: c.TEXT_3, lineHeight: 1.5 };

const OnboardingStepsTable = (props) => {
  const { data, setData, disabled, t, isRTL, hideTitle } = props;
  const s = data || {};
  const rtl = isRTL === 'rtl';
  const dir = rtl ? 'rtl' : 'ltr';

  const modes = api.resolveStepModes(s);
  const order = api.resolveStepOrder(s);
  const movable = order.filter((id) => !PINNED[id]);

  // On-page names of the legacy switches, so the Default option and the
  // warnings quote the same words the admin sees on the switches above.
  const switchLabel = (key) => {
    switch (key) {
      case 'license_image_required': return t('license_image_required', { defaultValue: 'License Image Required' });
      case 'imageIdApproval': return t('settings_label6', { defaultValue: 'Verify Id / Passport' });
      case 'carType_required': return t('carType_required', { defaultValue: 'Car Is Required' });
      case 'term_required': return t('term_required', { defaultValue: 'Term Required' });
      case 'bank_fields': return t('bank_fields', { defaultValue: 'Bank Reg Fields' });
      case 'tinRequiredForOnline': return t('tin_required_for_online', { defaultValue: 'Require TIN before a driver can go online' });
      case 'onboardingTinStepEnabled': return t('onboarding_tin_step_short', { defaultValue: 'TIN step default' });
      default: return key;
    }
  };

  // The legacy switch a step's Default follows (null = fixed default).
  const followsSwitch = (id) => {
    switch (id) {
      case 'license':
      case 'ownership': return 'license_image_required';
      case 'idPhoto': return 'imageIdApproval';
      case 'vehicle':
      case 'carPhoto': return 'carType_required';
      case 'tin': return s.tinRequiredForOnline === true ? 'tinRequiredForOnline' : 'onboardingTinStepEnabled';
      case 'bank': return 'bank_fields';
      case 'terms': return 'term_required';
      default: return null;
    }
  };

  const modeLabel = (mode) => {
    switch (mode) {
      case 'required': return t('onboarding_step_mode_required', { defaultValue: 'Required' });
      case 'optional': return t('onboarding_step_mode_optional', { defaultValue: 'Optional' });
      default: return t('onboarding_step_mode_off', { defaultValue: 'Hidden' });
    }
  };

  const modeChip = (mode) => {
    if (mode === 'required') return <StatusChip tone="success" icon={<CheckRoundedIcon />} label={modeLabel('required')} />;
    if (mode === 'optional') return <StatusChip tone="info" icon={<RemoveRoundedIcon />} label={modeLabel('optional')} />;
    return <StatusChip tone="neutral" icon={<VisibilityOffOutlinedIcon />} label={modeLabel('off')} />;
  };

  const stepLabel = (id) => {
    switch (id) {
      case 'license': return t('onboarding_step_license', { defaultValue: 'Driving licence photo' });
      case 'ownership': return t('onboarding_step_ownership', { defaultValue: 'Car ownership certificate (Libre)' });
      case 'idPhoto': return t('onboarding_step_id_photo', { defaultValue: 'ID card / passport photo' });
      case 'vehicle': return t('onboarding_step_vehicle', { defaultValue: 'Vehicle (car type, plate, make/model)' });
      case 'carPhoto': return t('onboarding_step_car_photo', { defaultValue: 'Car photo' });
      case 'tin': return t('onboarding_step_tin', { defaultValue: 'TIN number' });
      case 'bank': return t('onboarding_step_bank', { defaultValue: 'Payout method (bank / Telebirr)' });
      case 'terms': return t('onboarding_step_terms', { defaultValue: 'Terms & rules' });
      case 'extras': return t('onboarding_step_extras', { defaultValue: 'Extras (selfie, insurance)' });
      default: return id;
    }
  };

  const stepDesc = (id) => {
    switch (id) {
      case 'license': return t('onboarding_step_license_desc', { defaultValue: 'Photo of the front of the driving licence.' });
      case 'ownership': return t('onboarding_step_ownership_desc', { defaultValue: 'Photo of the vehicle ownership certificate (Libre).' });
      case 'idPhoto': return t('onboarding_step_id_photo_desc', { defaultValue: 'Photo of a national / Kebele ID or passport.' });
      case 'vehicle': return t('onboarding_step_vehicle_desc', { defaultValue: 'Car type, plate number, make and model of the car.' });
      case 'carPhoto': return t('onboarding_step_car_photo_desc', { defaultValue: 'Photo of the car; belongs to the vehicle above.' });
      case 'tin': return t('onboarding_step_tin_desc', { defaultValue: 'Tax identification number (10 digits).' });
      case 'bank': return t('onboarding_step_bank_desc', { defaultValue: 'Where the driver receives payouts.' });
      case 'terms': return t('onboarding_step_terms_desc', { defaultValue: 'Accept the terms and driver rules (customers see this step too).' });
      case 'extras': return t('onboarding_step_extras_desc', { defaultValue: 'Optional tiles + Finish. Always last, never required.' });
      default: return '';
    }
  };

  const defaultOptionLabel = (id) => {
    const legacy = api.legacyStepMode(id, s);
    const sw = followsSwitch(id);
    const base = t('onboarding_step_mode_default', { defaultValue: 'Default' });
    if (!sw) return base + ' (' + modeLabel(legacy) + ')';
    return base + ' (' + modeLabel(legacy) + ' — ' + t('onboarding_step_follows', { name: switchLabel(sw), defaultValue: "follows '{{name}}'" }) + ')';
  };

  // Why the resolver's coherence rules changed the admin's choice, if they did.
  const overrideReason = (id) => {
    const chosen = api.configuredStepMode(id, s) || api.legacyStepMode(id, s);
    if (modes[id] === chosen) return null;
    switch (id) {
      case 'tin': return t('onboarding_step_forced_tin', { name: switchLabel('tinRequiredForOnline'), defaultValue: "Required while '{{name}}' is on" });
      case 'carPhoto': return t('onboarding_step_forced_car_photo', { defaultValue: 'Hidden while Vehicle is hidden' });
      case 'extras': return t('onboarding_step_forced_extras', { defaultValue: 'Extras is never required' });
      default: return null;
    }
  };

  const blockedWarning = (id) => {
    if (id === 'vehicle' && modes.vehicle !== 'required') {
      return t('onboarding_step_warn_vehicle', { defaultValue: 'Regular bookings are matched by car type: a driver with no car type on file is offered bookings of every car type. Donkey Meter and the offline meter are not affected.' });
    }
    if (id === 'tin' && modes.tin === 'required' && s.tinRequiredForOnline !== true) {
      return t('onboarding_step_warn_tin', { name: switchLabel('tinRequiredForOnline'), defaultValue: "Required here means asked in the wizard. Going online still follows '{{name}}'." });
    }
    return null;
  };

  const setMode = (id, value) => {
    const key = api.onboardingStepSettingKey(id);
    const next = { ...s };
    if (value === DEFAULT) delete next[key]; else next[key] = value;
    setData(next);
  };

  const move = (id, delta) => {
    const i = movable.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= movable.length) return;
    const next = movable.slice();
    next[i] = movable[j];
    next[j] = id;
    // Always store the full sanitized list (name first, carPhoto after
    // vehicle, extras last) so the app and the server read the same order.
    setData({ ...s, onboardingStepOrder: api.resolveStepOrder({ onboardingStepOrder: next }).join(',') });
  };

  const resetOrder = () => {
    const next = { ...s };
    delete next.onboardingStepOrder;
    setData(next);
  };

  const cellSx = { verticalAlign: 'top', py: 1.25 };
  const nameCell = (title, desc, warning) => (
    <TableCell sx={cellSx}>
      <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: c.INK }}>{title}</Typography>
      {desc ? <Typography sx={mutedSx}>{desc}</Typography> : null}
      {warning ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.5, color: c.WARNING_TEXT, fontSize: 13, lineHeight: 1.5 }}>
          <WarningAmberRoundedIcon aria-hidden="true" sx={{ fontSize: 17, mt: '1px', flex: 'none' }} />
          <span>{warning}</span>
        </Box>
      ) : null}
    </TableCell>
  );

  return (
    <Box dir={dir} sx={{ my: 1, opacity: disabled ? 0.6 : 1 }}>
      {hideTitle ? null : (
        <Typography component="h3" sx={{ fontWeight: 700, fontSize: 16, color: c.INK }}>
          {t('onboarding_wizard_steps', { defaultValue: 'Wizard steps (drivers)' })}
        </Typography>
      )}
      <Typography sx={{ ...mutedSx, mb: 1 }}>
        {t('onboarding_wizard_steps_help', { defaultValue: 'Required = shown, must be completed; Optional = shown with "Skip for now"; Hidden = not shown. Default follows the matching switch above. The app and the server apply the Effective column.' })}
      </Typography>
      <Box sx={{ overflowX: 'auto', border: `1px solid ${c.OUTLINE}`, borderRadius: '10px' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('onboarding_step_col_step', { defaultValue: 'Step' })}</TableCell>
              <TableCell>{t('onboarding_step_col_mode', { defaultValue: 'Mode' })}</TableCell>
              <TableCell>{t('onboarding_step_col_effective', { defaultValue: 'Effective' })}</TableCell>
              <TableCell>{t('onboarding_step_col_order', { defaultValue: 'Order' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {order.map((id) => {
              if (id === 'name') {
                return (
                  <TableRow key="name">
                    {nameCell(
                      t('onboarding_step_name', { defaultValue: 'Name' }),
                      t('onboarding_step_name_desc', { defaultValue: 'First and last name. Always first, always required (drivers and customers).' })
                    )}
                    <TableCell sx={cellSx}>
                      <Typography sx={mutedSx}>{t('onboarding_step_always_required', { defaultValue: 'Always required' })}</Typography>
                    </TableCell>
                    <TableCell sx={cellSx}>{modeChip('required')}</TableCell>
                    <TableCell sx={cellSx}>
                      <Typography sx={mutedSx}>{t('onboarding_step_pinned_first', { defaultValue: 'Always first' })}</Typography>
                    </TableCell>
                  </TableRow>
                );
              }
              const configured = api.configuredStepMode(id, s) || DEFAULT;
              const reason = overrideReason(id);
              const warning = blockedWarning(id);
              const idx = movable.indexOf(id);
              const upLabel = `${t('onboarding_step_move_up', { defaultValue: 'Move up' })}: ${stepLabel(id)}`;
              const downLabel = `${t('onboarding_step_move_down', { defaultValue: 'Move down' })}: ${stepLabel(id)}`;
              return (
                <TableRow key={id}>
                  {nameCell(stepLabel(id), stepDesc(id), warning)}
                  <TableCell sx={{ ...cellSx, minWidth: 240 }}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      id={'onboarding-step-mode-' + id}
                      value={configured}
                      disabled={disabled}
                      onChange={(e) => setMode(id, e.target.value)}
                      SelectProps={{ MenuProps: { dir } }}
                      inputProps={{ 'aria-label': `${t('onboarding_step_col_mode', { defaultValue: 'Mode' })}: ${stepLabel(id)}` }}
                      sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
                    >
                      <MenuItem value={DEFAULT} sx={{ whiteSpace: 'normal' }}>{defaultOptionLabel(id)}</MenuItem>
                      <MenuItem value="required" disabled={id === 'extras'}>{modeLabel('required')}</MenuItem>
                      <MenuItem value="optional">{modeLabel('optional')}</MenuItem>
                      <MenuItem value="off">{modeLabel('off')}</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    {modeChip(modes[id])}
                    {reason ? <Typography sx={{ ...mutedSx, mt: 0.5 }}>{reason}</Typography> : null}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>
                    {id === 'extras' ? (
                      <Typography sx={mutedSx}>{t('onboarding_step_pinned_last', { defaultValue: 'Always last' })}</Typography>
                    ) : id === 'carPhoto' ? (
                      <Typography sx={mutedSx}>{t('onboarding_step_moves_with_vehicle', { defaultValue: 'Moves with Vehicle' })}</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={upLabel}>
                          <span>
                            <IconButton size="small" aria-label={upLabel} disabled={disabled || idx <= 0} onClick={() => move(id, -1)}>
                              <ArrowUpwardRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={downLabel}>
                          <span>
                            <IconButton size="small" aria-label={downLabel} disabled={disabled || idx < 0 || idx >= movable.length - 1} onClick={() => move(id, 1)}>
                              <ArrowDownwardRoundedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1.5, mt: 1 }}>
        <Typography sx={{ ...mutedSx, flex: '1 1 320px' }}>
          {t('onboarding_wizard_steps_note', { defaultValue: 'This table is the rule for drivers everywhere: the wizard, Finish, the go-online switch, the driver-home alerts and dispatch all follow it — the switches above only set each row\'s Default. Only a Required step can block a driver; Optional and Hidden never do. Customers get Name and Terms only; the Terms mode applies to them too.' })}
        </Typography>
        <Button variant="outlined" color="neutral" size="small" startIcon={<RestartAltRoundedIcon />} disabled={disabled || !s.onboardingStepOrder} onClick={resetOrder}>
          {t('onboarding_step_reset_order', { defaultValue: 'Reset order' })}
        </Button>
      </Box>
    </Box>
  );
};

export default OnboardingStepsTable;
